/* =========================================================================
   iUROP — Buscador overlay (predictive search) para Gadea Loreak
   - Se abre desde cualquier [data-search-toggle] del header (id compartido).
   - Usa /search/suggest (Section Rendering API) -> sección iurop-predictive-search.
   - Sincroniza el input del overlay con un input externo del header
     ([data-search-input]) si existe; si no, muestra su propio campo en desktop.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.getElementById('iurop-search');
  if (!root) return;

  var panel = root.querySelector('[data-search-panel]');
  var overlayInput = root.querySelector('[data-search-overlay-input]');
  var resultsEl = root.querySelector('[data-search-results]');
  var defaultEl = root.querySelector('[data-search-default]');
  var sectionId = root.getAttribute('data-predictive-section') || 'iurop-predictive-search';
  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var MIN_CHARS = 2;

  var externalInputs = [];
  var currentOpener = null;
  var lastQuery = '';
  var controller = null;
  var debounceTimer = null;
  var suppressReopen = false;

  // ≤989px el header muestra el icono de búsqueda (sin campo inline): el overlay
  // usa su propio campo y se comporta como tarjeta flotante. ≥990px usa el input
  // del header. El umbral coincide con el breakpoint del header.
  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /* ---- posición del panel bajo el header ---- */
  function positionPanel() {
    var top = 0;
    var anchor = null;
    if (currentOpener && currentOpener.closest) {
      anchor = currentOpener.closest('.shopify-section, header, [data-header]');
    }
    if (!anchor) {
      anchor = document.querySelector('[data-search-anchor], header, [data-header]');
    }
    if (anchor) {
      var r = anchor.getBoundingClientRect();
      top = Math.max(0, r.bottom);
      // Móvil (Figma 15699-10861): el buscador SUSTITUYE la píldora del header,
      // así que arranca donde EMPIEZA la píldora (justo bajo la barra de
      // anuncios), no bajo el header. La píldora se oculta por CSS.
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        var pill = anchor.querySelector('.iurop-header__panel');
        if (pill) top = Math.max(0, pill.getBoundingClientRect().top);
      }
    }
    if (!top) top = 112;
    root.style.setProperty('--iurop-search-top', top + 'px');
  }

  /* ---- inputs externos (buscador del header en desktop) ---- */
  function collectExternalInputs() {
    externalInputs = Array.prototype.slice
      .call(document.querySelectorAll('[data-search-input]'))
      .filter(function (el) { return !el.hasAttribute('data-search-overlay-input'); });
    root.classList.toggle('iurop-search--standalone', externalInputs.length === 0);
  }

  function syncInputs(value, except) {
    var list = externalInputs.concat(overlayInput ? [overlayInput] : []);
    list.forEach(function (el) {
      if (el !== except && el.value !== value) el.value = value;
    });
  }

  /* ---- abrir / cerrar ---- */
  function open() {
    if (!root.hidden && root.classList.contains('is-open')) return;
    collectExternalInputs();
    positionPanel();
    root.hidden = false;
    // reflow para permitir la transición
    // eslint-disable-next-line no-unused-expressions
    root.offsetHeight;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('iurop-search-locked');
    // Hook para el header: cuadra las esquinas inferiores de la píldora y sube el
    // header por encima del backdrop transparente (ver sections/header.liquid).
    document.body.classList.add('iurop-search-open');
    document.addEventListener('keydown', onKeydown, true);
    window.addEventListener('resize', positionPanel);
    window.addEventListener('scroll', positionPanel, { passive: true });

    var target = (!isMobile() && externalInputs.length) ? externalInputs[0] : overlayInput;
    if (target) {
      setTimeout(function () {
        target.focus();
        if (target.setSelectionRange && target.value) {
          try { target.setSelectionRange(target.value.length, target.value.length); } catch (e) {}
        }
      }, 60);
    }
    // si ya había texto (del input del header) lanza la búsqueda
    if (target && target.value.trim().length >= MIN_CHARS) runSearch(target.value);
  }

  function close() {
    if (!root.classList.contains('is-open')) return; // ya cerrado/cerrándose

    // Evita el BUCLE de reapertura por foco: si el opener es el input del header,
    // su handler `focus` reabriría el overlay. Al cerrar le quitamos el foco; el
    // flag cubre la re-focalización SÍNCRONA que dispara ese blur.
    suppressReopen = true;
    var active = document.activeElement;
    if (active && active.matches && active.matches('[data-search-input]') && active.blur) {
      active.blur();
    }
    suppressReopen = false;

    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('iurop-search-locked');
    document.removeEventListener('keydown', onKeydown, true);
    window.removeEventListener('resize', positionPanel);
    window.removeEventListener('scroll', positionPanel);

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', finish);
      if (root.classList.contains('is-open')) return; // reabierto durante el cierre
      root.hidden = true;
      document.body.classList.remove('iurop-search-open');
    };
    panel.addEventListener('transitionend', finish);
    setTimeout(finish, 320);
  }

  function toggle(opener) {
    currentOpener = opener || currentOpener;
    if (root.hidden) open(); else close();
  }

  /* ---- vistas ---- */
  function showDefault() {
    if (resultsEl) { resultsEl.hidden = true; resultsEl.innerHTML = ''; }
    if (defaultEl) defaultEl.hidden = false;
    root.classList.remove('is-searching');
  }
  function showResults() {
    if (defaultEl) defaultEl.hidden = true;
    if (resultsEl) resultsEl.hidden = false;
    root.classList.add('is-searching');
  }

  /* ---- predictive fetch ---- */
  function runSearch(value) {
    var q = (value || '').trim();
    syncInputs(q);
    if (q.length < MIN_CHARS) { lastQuery = ''; showDefault(); return; }
    if (q === lastQuery && resultsEl && resultsEl.innerHTML) { showResults(); return; }
    lastQuery = q;
    showResults();
    if (resultsEl) resultsEl.classList.add('is-loading');

    if (controller) controller.abort();
    controller = new AbortController();

    var url = routeRoot.replace(/\/$/, '') +
      '/search/suggest?q=' + encodeURIComponent(q) +
      '&section_id=' + encodeURIComponent(sectionId) +
      '&resources[type]=product,collection,query,article' +
      '&resources[limit]=8' +
      // QA: "DESDE MENU Y BUSCADOR QUE NO SALGA PRODUCTOS AGOTADOS".
      // 'hide' los excluye YA en el servidor (antes 'last', que solo los
      // mandaba al final). La sección iurop-predictive-search vuelve a
      // filtrarlos por si otro caller pidiera 'show'/'last'.
      '&resources[options][unavailable_products]=hide';

    fetch(url, { signal: controller.signal })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!resultsEl) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-predictive-content]');
        resultsEl.innerHTML = content ? content.outerHTML : (doc.body ? doc.body.innerHTML : '');
        resultsEl.classList.remove('is-loading');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (resultsEl) resultsEl.classList.remove('is-loading');
      });
  }

  function debouncedSearch(value) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { runSearch(value); }, 220);
  }

  function submitSearch(value) {
    var q = (value || '').trim();
    if (!q) return;
    window.location.href = routeRoot.replace(/\/$/, '') +
      '/search?q=' + encodeURIComponent(q) + '&options[prefix]=last';
  }

  /* ---- eventos ---- */
  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
    }
  }

  // abrir/cerrar desde el icono del header
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-search-toggle]');
    if (t) { e.preventDefault(); toggle(t); }
  });

  // El drawer del carrito (z 200) y el modal quick-view (z 210) se abren DETRÁS
  // de este overlay (z 900): al añadir al carrito cerramos el buscador para que
  // queden visibles. Las tarjetas de bestsellers son las product cards estándar:
  // su quick-add ([data-iurop-quick-add], iurop-product-card.js) y el quick-view
  // ([data-qv-atc], iurop-quick-view.js) despachan 'cart:updated'/'cart-drawer:open'.
  function closeForCart() {
    if (root.classList.contains('is-open')) close();
  }
  document.addEventListener('cart:added', closeForCart);
  document.addEventListener('cart:refresh', closeForCart);
  document.addEventListener('cart:updated', closeForCart);
  document.addEventListener('cart-drawer:open', closeForCart);

  // clic en backdrop o botón cerrar
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); return; }
    var clear = e.target.closest('[data-search-clear]');
    if (clear) {
      e.preventDefault();
      if (overlayInput && overlayInput.value) {
        overlayInput.value = '';
        syncInputs('');
        runSearch('');
        overlayInput.focus();
      } else {
        close();
      }
      return;
    }
    // "+" de producto con variantes: el modal quick-view (z 210) abre por debajo
    // de este overlay (z 900) → cerramos el buscador y dejamos que el handler
    // global de iurop-quick-view.js (delegado en document, se ejecuta después
    // en la fase de burbuja) abra el modal. Sin preventDefault.
    if (e.target.closest('[data-iurop-quick-view]')) { close(); }
  });

  // escribir en cualquier input de búsqueda (overlay o header)
  document.addEventListener('input', function (e) {
    var el = e.target.closest('[data-search-input]');
    if (!el) return;
    if (root.hidden) { currentOpener = el; open(); }
    debouncedSearch(el.value);
  });

  // Enter en un input de búsqueda -> página de resultados
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var el = e.target.closest('[data-search-input]');
    if (!el) return;
    e.preventDefault();
    submitSearch(el.value);
  });

  // focus en el input del header (desktop) abre el overlay
  document.addEventListener('focusin', function (e) {
    if (suppressReopen) return; // no reabrir por la re-focalización del cierre
    var el = e.target.closest('[data-search-input]');
    if (el && !el.hasAttribute('data-search-overlay-input') && root.hidden) {
      currentOpener = el;
      open();
    }
  });

  // submit del formulario del overlay
  var form = root.querySelector('[data-search-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitSearch(overlayInput ? overlayInput.value : '');
    });
  }
})();
